import type { TrialBalanceLine } from '@/types/accounting';

export interface TrialBalanceNode extends TrialBalanceLine {
  children?: TrialBalanceNode[];
  /** Глибина у дереві — для відступів та сортування. */
  depth: number;
  /** Кількість дочірніх (рекурсивно) — для UI counter. */
  descendantCount: number;
  /** true — рядок-група, синтезований з ChartOfAccount (без власних рухів, суми = sum(children)). */
  isSynthetic?: boolean;
}

/** Метаінформація про рахунок з ChartOfAccount: батько за кодом + назва (для синтетичних груп). */
export interface AccountMeta {
  code: string;
  name: string;
  parentCode?: string;
  isGroup?: boolean;
}

/**
 * Будує дерево рахунків для ОСВ.
 *
 * Стратегія:
 * 1. Якщо передано <c>acqAccountMeta</c> (мапа з реальною ієрархією ChartOfAccount.ParentId) —
 *    використовуємо її. Для рядків TB, чий код не у TB-наборі (наприклад, 1121 без 112), —
 *    синтезуємо батьківські групи з нульовими власними сумами; після build агрегуємо
 *    суми на синтетичні групи як sum(children).
 * 2. Якщо мапи немає — fallback на prefix-match (281 → батько 28 у наборі).
 *
 * Не модифікує суми НЕсинтетичних рядків — backend повертає їх вже агрегованими per-code.
 */
export function acqBuildTrialBalanceTree(
  acqLines: TrialBalanceLine[],
  acqAccountMeta?: Map<string, AccountMeta>,
): TrialBalanceNode[] {
  if (acqLines.length === 0) return [];

  // 1. Створюємо ноди з реальних TB-рядків.
  const acqByCode = new Map<string, TrialBalanceNode>();
  for (const acqRow of acqLines) {
    acqByCode.set(acqRow.accountCode, { ...acqRow, depth: 0, descendantCount: 0 });
  }

  // 2. Синтезуємо батьківські групи з ChartOfAccount (якщо мапа передана).
  if (acqAccountMeta) {
    const acqToVisit = [...acqByCode.keys()];
    for (const acqCode of acqToVisit) {
      let acqCurrent = acqAccountMeta.get(acqCode);
      while (acqCurrent?.parentCode) {
        const acqParentCode = acqCurrent.parentCode;
        if (!acqByCode.has(acqParentCode)) {
          const acqParentMeta = acqAccountMeta.get(acqParentCode);
          if (!acqParentMeta) break;
          acqByCode.set(acqParentCode, {
            accountCode: acqParentCode,
            accountName: acqParentMeta.name,
            isGroup: true,
            openingDebit: 0, openingCredit: 0,
            turnoverDebit: 0, turnoverCredit: 0,
            closingDebit: 0, closingCredit: 0,
            depth: 0, descendantCount: 0,
            isSynthetic: true,
          });
        }
        acqCurrent = acqAccountMeta.get(acqParentCode);
      }
    }
  }

  // 3. Будуємо дерево: для кожної ноди шукаємо батька.
  const acqRoots: TrialBalanceNode[] = [];
  for (const acqNode of acqByCode.values()) {
    const acqParentCode = acqAccountMeta
      ? acqAccountMeta.get(acqNode.accountCode)?.parentCode
      : undefined;
    const acqParent = acqParentCode
      ? acqByCode.get(acqParentCode)
      : acqFindLongestPrefixParent(acqNode.accountCode, acqByCode);
    if (acqParent) {
      acqParent.children = acqParent.children ?? [];
      acqParent.children.push(acqNode);
    } else {
      acqRoots.push(acqNode);
    }
  }

  // 4. Сортування + depth + descendantCount.
  acqRoots.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  for (const acqRoot of acqRoots) {
    acqAssignDepthAndCount(acqRoot, 0);
  }

  // 5. Агрегуємо суми для синтетичних груп як sum(children) (BAS-стиль ОСВ).
  for (const acqRoot of acqRoots) {
    acqAggregateSyntheticSums(acqRoot);
  }

  return acqRoots;
}

function acqFindLongestPrefixParent(
  acqCode: string,
  acqByCode: Map<string, TrialBalanceNode>,
): TrialBalanceNode | null {
  for (let acqLen = acqCode.length - 1; acqLen >= 1; acqLen--) {
    const acqPrefix = acqCode.substring(0, acqLen);
    const acqParent = acqByCode.get(acqPrefix);
    if (acqParent) return acqParent;
  }
  return null;
}

function acqAssignDepthAndCount(acqNode: TrialBalanceNode, acqDepth: number): number {
  acqNode.depth = acqDepth;
  if (!acqNode.children || acqNode.children.length === 0) {
    acqNode.descendantCount = 0;
    return 1;
  }
  acqNode.children.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  let acqCount = 0;
  for (const acqChild of acqNode.children) {
    acqCount += acqAssignDepthAndCount(acqChild, acqDepth + 1);
  }
  acqNode.descendantCount = acqCount;
  return acqCount + 1;
}

function acqAggregateSyntheticSums(acqNode: TrialBalanceNode): void {
  if (!acqNode.children || acqNode.children.length === 0) return;
  for (const acqChild of acqNode.children) {
    acqAggregateSyntheticSums(acqChild);
  }
  if (acqNode.isSynthetic) {
    acqNode.openingDebit = acqNode.children.reduce((s, c) => s + c.openingDebit, 0);
    acqNode.openingCredit = acqNode.children.reduce((s, c) => s + c.openingCredit, 0);
    acqNode.turnoverDebit = acqNode.children.reduce((s, c) => s + c.turnoverDebit, 0);
    acqNode.turnoverCredit = acqNode.children.reduce((s, c) => s + c.turnoverCredit, 0);
    acqNode.closingDebit = acqNode.children.reduce((s, c) => s + c.closingDebit, 0);
    acqNode.closingCredit = acqNode.children.reduce((s, c) => s + c.closingCredit, 0);
  }
}

/** Збирає всі ключі (accountCode) дерева — для defaultExpandedRowKeys. */
export function acqCollectAllKeys(acqNodes: TrialBalanceNode[]): string[] {
  const acqKeys: string[] = [];
  function acqWalk(acqList: TrialBalanceNode[]) {
    for (const acqN of acqList) {
      acqKeys.push(acqN.accountCode);
      if (acqN.children) acqWalk(acqN.children);
    }
  }
  acqWalk(acqNodes);
  return acqKeys;
}

/** Максимальна глибина дерева (0-indexed). 0 — лише корені; 2 — корінь→дитина→онук. */
export function acqMaxDepth(acqNodes: TrialBalanceNode[]): number {
  let acqMax = 0;
  function acqWalk(acqList: TrialBalanceNode[]) {
    for (const acqN of acqList) {
      if (acqN.depth > acqMax) acqMax = acqN.depth;
      if (acqN.children) acqWalk(acqN.children);
    }
  }
  acqWalk(acqNodes);
  return acqMax;
}

/**
 * Збирає ключі вузлів, які мають бути РОЗГОРНУТІ для відображення рівня <c>acqLevel</c>.
 * Це всі вузли з depth &lt; acqLevel (бо саме вони мають дітей видимими).
 *
 * Приклади:
 * - acqLevel=0: жоден ключ (всі корені згорнуті) → видно лише корені.
 * - acqLevel=1: розгорнуті лише корені (depth=0) → видно корені + їхніх дітей.
 * - acqLevel=∞: розгорнуто все.
 */
export function acqCollectKeysUpToLevel(acqNodes: TrialBalanceNode[], acqLevel: number): string[] {
  const acqKeys: string[] = [];
  function acqWalk(acqList: TrialBalanceNode[]) {
    for (const acqN of acqList) {
      if (acqN.depth < acqLevel) acqKeys.push(acqN.accountCode);
      if (acqN.children) acqWalk(acqN.children);
    }
  }
  acqWalk(acqNodes);
  return acqKeys;
}
